import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  Grid,
  Alert,
  AlertTitle,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import { FormProvider, RHFUploadAvatar } from '../hook-form';
import LinearProgressBar from '../progress-bar/progressBar.component';
import BrandLimitsInfoDialog from '../BrandLimitsInfoDialog';

const WorkPlaceBox = ({ handleClose, handleSuccess }) => {
  const navigate = useNavigate();
  const orgDetails = CommonHelper.GetBrandInfoFromLocalStorage('initial-load');
  const isReseller = CommonHelper.GetBrandInfoFromLocalStorage('is_reseller');
  const maxFileSizeInBytes = 10485760;

  const { t: translate } = useTranslation();
  const { showToast } = useToaster();

  const [imageUrl, setImageUrl] = useState('');
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [brandName, setBrandName] = useState('');
  const [description, setDescription] = useState('');
  const [numberOfUsersPerBrand, setNumberOfUsersPerBrand] = useState(0);
  const [numberOfSocialAccountsPerBrand, setNumberOfSocialAccountsPerBrand] = useState(0);
  const [numberOfTextGenerationTokenPerBrand, setNumberOfTextGenerationTokenPerBrand] = useState(0);
  const [numberOfImageGenerationTokenPerBrand, setNumberOfImageGenerationTokenPerBrand] = useState(0);
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [timeZone, setTimeZone] = useState('UTC');
  const [showLimitsInfoDialog, setShowLimitsInfoDialog] = useState(false);
  const [forceShowLimitsDialog, setForceShowLimitsDialog] = useState(false);
  const [limitsWarningTriggered, setLimitsWarningTriggered] = useState(false);

// Helper to check if limits are effectively "unlimited / dangerous"
const areLimitsEffectivelyUnlimited = () => {
  return (
    isReseller &&
   ( Number(numberOfUsersPerBrand) === 0 ||
    Number(numberOfSocialAccountsPerBrand) === 0)
  );
};

  const defaultValues = {
    imageUrl: imageUrl || '',
    description: description || '',
    brandName: brandName || '',
  };

  useEffect(() => {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (localTimeZone) setTimeZone(localTimeZone);
    generateFormModelSchema();
  }, []);

  const generateFormModelSchema = () => {
    let fields = [ValidationTool.GenerateSchema('brandName', translate('brand.brand_name'), 'TEXT', true)];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const methods = useForm({ defaultValues });
  const { handleSubmit } = methods;

  const successCallback = (data, message) => {
    setLoading(false);
    message && showToast(message, 's');
    handleSuccess();
  };

  const failureCallback = (message) => {
    setLoading(false);
    const errorMessage = message?.toLowerCase() || '';
    showToast(message, 'e');
    if (errorMessage.includes('set higher limit than available')) {
    setShowLimitsInfoDialog(true);
    // Optional: make alert more visible / warning style immediately
    setLimitsWarningTriggered(true);
  }
  };

const handleSubmitForm = () => {
  const userForm = { brandName };
  const { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);

  if (!isValidForm) {
    setFormValidationErrors(formValidationErrors);
    return;
  }

  // Reseller mode + both critical limits are 0 → force dialog
  if (areLimitsEffectivelyUnlimited()) {
    setForceShowLimitsDialog(true);
    setLimitsWarningTriggered(true);
    return; // ← prevent submission
  }

  // Normal flow
  setLoading(true);
  setFormValidationErrors({});
  createNewBrand();
};
  const createNewBrand = () => {
    const newBrand = {
      brand_name: brandName,
      description,
      brand_image: imageUrl,
      timezone: timeZone,
      language: orgDetails?.language || 'english',
      brand_limit_info: {
        social_account_limit: numberOfSocialAccountsPerBrand ? parseInt(numberOfSocialAccountsPerBrand) : 0,
        users_limit: numberOfUsersPerBrand ? parseInt(numberOfUsersPerBrand) : 0,
        ai_content_tokens: numberOfTextGenerationTokenPerBrand ? parseInt(numberOfTextGenerationTokenPerBrand) * 100 : 0,
        ai_image_token: numberOfImageGenerationTokenPerBrand ? parseInt(numberOfImageGenerationTokenPerBrand) : 0,
      },
    };

    HttpServices.Post({
      url: userModuleURL.createNewBrand,
      body: newBrand,
      successCallback,
      failureCallback,
    });
  };

 const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name.toLowerCase()) {
      case 'brandname':
        setBrandName(value);
        break;
      case 'description':
        setDescription(value);
        break;
      case 'numberofusersperbrand':
        setNumberOfUsersPerBrand(value);
        break;
      case 'numberofsocialaccountsperbrand':
        setNumberOfSocialAccountsPerBrand(value);
        break;
      case 'numberoftextgenerationtokenperbrand':
        setNumberOfTextGenerationTokenPerBrand(value);
        break;
      case 'numberofimagegenerationtokenperbrand':
        setNumberOfImageGenerationTokenPerBrand(value);
        break;
      default:
        break;
    }
  };

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) handleFileUpload(file);
  };

  const handleDeleteBrandImage = () => setImageUrl('');

  const fileUploadSuccessCallback = (data) => {
    if (data?.media_data?.[0]?.media_url) {
      setImageUrl(data.media_data[0].media_url);
    }
  };

   const handleFileUpload = (file) => {
    let isFileInCorrectFormat = CommonHelper.SupportedImageFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadprogress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadprogress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((result) => result)
          .then((response) => {
            setFileUploadprogress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(response.data.data, translate('common.file_uploaded_successfully'));
            else showToast(translate('common.something_went_wrong'), 'e');
            setFileUploadprogress(0);
          })
          .catch((error) => {
            setFileUploadprogress(0);
            showToast(error.toString(), 'e');
          });
      } else {
        showToast(`${translate('brand.max_image_size')} 10MB. ${translate('brand.please_select_below')}`, 'e');
      }
    } else {
      showToast(
        `${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate(
          'common.file_formats'
        )}. ${translate('common.selected_file_not_in_format')}`,
        'e'
      );
    }
  };
  const handleLimitsDialogClose = () => {
  setForceShowLimitsDialog(false);
  // Keep limitsWarningTriggered = true so the alert stays "warning" style
};
const handleGoToResellerSettings = () => {
  setShowLimitsInfoDialog(false);
  setForceShowLimitsDialog(false);
  navigate('/handle-reseller');
  handleClose();
};
  return (
    <>
      <Dialog open maxWidth="md" fullWidth>
        <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
          <DialogTitle>
            <Typography variant="h4">{translate('brand.create_brand')}</Typography>
          </DialogTitle>

          <DialogContent>
            <Card sx={{ p:1, textAlign: 'center', mb: 2 }}>
              <RHFUploadAvatar
                name="imageUrl"
                accept="image/*"
                onDrop={handleDrop}
                handleDeleteProfiles={handleDeleteBrandImage}
                url={imageUrl}
                helperText={
                  <Typography variant="caption" align="center" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    {translate('brand.allowed')} *.jpeg, *.jpg, *.png, *.gif<br />
                    {translate('brand.max_size_of')} 5Mb
                  </Typography>
                }
              />
            </Card>

            {fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}

            <Stack spacing={3} sx={{ mt: 2, mx: 1 }}>
              <Grid container spacing={2}>
                {/* Brand name – always visible */}
                <Grid item xs={12}>
                  <TextField
                    label={translate('brand.brand_name')}
                    name="brandName"
                    value={brandName}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!formValidationErrors?.brandName}
                    helperText={formValidationErrors?.brandName || ' '}
                  />
                </Grid>

                {/* Description – always visible */}
                <Grid item xs={12}>
                  <TextField
                    label={translate('common.description')}
                    name="description"
                    value={description}
                    multiline
                    minRows={2}
                    fullWidth
                    onChange={handleInputChange}
                  />
                </Grid>

                {isReseller && (
                  <>

                    {/* Limits fields */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translate('common.number_of_users_per_brand')}
                        name="numberOfUsersPerBrand"
                        value={numberOfUsersPerBrand}
                        type="number"
                        fullWidth
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translate('common.number_of_social_accounts_per_brand')}
                        name="numberOfSocialAccountsPerBrand"
                        value={numberOfSocialAccountsPerBrand}
                        type="number"
                        fullWidth
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translate('common.number_of_text_generation_token_per_brand')}
                        name="numberOfTextGenerationTokenPerBrand"
                        value={numberOfTextGenerationTokenPerBrand}
                        type="number"
                        fullWidth
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={translate('common.number_of_image_generation_token_per_brand')}
                        name="numberOfImageGenerationTokenPerBrand"
                        value={numberOfImageGenerationTokenPerBrand}
                        type="number"
                        fullWidth
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert
        severity={limitsWarningTriggered ? "warning" : "info"}
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        sx={{
          cursor: 'pointer',
          '&:hover': { opacity: 0.92 },
          borderColor: limitsWarningTriggered ? 'warning.main' : undefined,
        }}
        onClick={() => setShowLimitsInfoDialog(true)}
      >
        <AlertTitle>
          {limitsWarningTriggered
            ? translate('brand.please_set_limits_before_creating')
            : translate('common.about_brand_limits')}
        </AlertTitle>

        {limitsWarningTriggered ? (
          <>
            {translate('brand.both_limits_are_zero_warning') ||
              'You left both Users and Social Accounts limits at 0 — this allows unlimited usage per brand.'}
            <br />
            <strong>
              {translate('brand.set_at_least_one_limit_or_disable_reseller') ||
                'Please set reasonable limits or disable reseller mode.'}
            </strong>
          </>
        ) : (
          <>
            {translate('brand.limits_explanation_short') ||
              'You are in reseller mode — you can set usage limits per brand.'}
            <br />
            <strong>{translate('common.click_to_learn_more')}</strong>
          </>
        )}
      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" color="error" onClick={handleClose}>
              {translate('common.cancel')}
            </Button>
            <LoadingButton variant="contained" loading={isLoading} type="submit">
              {translate('brand.create_brand')}
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>

      {/* Info Dialog – shown when clicking the alert card (only visible when isReseller = true) */}
      <BrandLimitsInfoDialog
  open={showLimitsInfoDialog || forceShowLimitsDialog}
  onClose={forceShowLimitsDialog ? handleLimitsDialogClose : () => setShowLimitsInfoDialog(false)}
  onGoToResellerSettings={handleGoToResellerSettings}
/>
    </>
  );
};

export default WorkPlaceBox;